import React, { useCallback, useState } from 'react';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import { Printer } from 'lucide-react';
import { CertificatePDFData } from './types';
import CertificatePDFDocument from './PDFTemplate/index';

interface PreviewModalProps {
  certificate: CertificatePDFData;
  isOpen: boolean;
  onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ certificate, isOpen, onClose }) => {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useCallback(async () => {
    setIsPrinting(true);
    try {
      const blob = await pdf(
        <CertificatePDFDocument certificate={certificate} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'Impressão do certificado');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      let finished = false;
      let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
      const safeFinish = () => {
        if (finished) return;
        finished = true;
        if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
        URL.revokeObjectURL(url);
        iframe.remove();
        setIsPrinting(false);
      };

      iframe.onload = () => {
        const win = iframe.contentWindow;
        if (!win) {
          safeFinish();
          return;
        }
        win.addEventListener(
          'afterprint',
          () => {
            if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
            safeFinish();
          },
          { once: true }
        );
        win.focus();
        win.print();
        // Fallback se afterprint não existir (navegadores antigos)
        fallbackTimer = setTimeout(safeFinish, 120_000);
      };
    } catch (e) {
      console.error('Falha ao preparar impressão do certificado:', e);
      setIsPrinting(false);
    }
  }, [certificate]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      
      <div style={{
        backgroundColor: 'white',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <h2 style={{ margin: 0, color: '#0090d3' }}>
          Preview do Certificado #{certificate.id}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={handlePrint}
            disabled={isPrinting}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              backgroundColor: '#0090d3',
              color: 'white',
              border: 'none',
              borderRadius: '1rem',
              padding: '0.5rem 0.85rem',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: isPrinting ? 'wait' : 'pointer',
              opacity: isPrinting ? 0.75 : 1,
            }}
          >
            <Printer size={18} aria-hidden />
            {isPrinting ? 'A preparar…' : 'Imprimir'}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#0090D3',
              padding: '0.5rem'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      
      <div style={{ flex: 1, backgroundColor: '#f3f4f6', position: 'relative' }}>
        
        <PDFViewer 
          style={{ width: '100%', height: '100%' }}
          showToolbar={false}
        >
          <CertificatePDFDocument certificate={certificate} />
        </PDFViewer>
      </div>
    </div>
  );
};

export default PreviewModal; 